import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import Section from "../components/ui/Section";
import StatCard from "../components/ui/StatCard";
import { CONTACT } from "../config/site";
import { hero } from "../content/hero";
import { useState } from "react";
import DemoDialog from "../components/ui/DemoDialog";

export default function HeroHeader() {
  const theme = useTheme();
  const [openDemo, setOpenDemo] = useState(false);
  return (
    // ✅ خلفية الهيدر الآن تعتمد على اللون الرئيسي فقط (main → dark)
    <Box
      sx={{
        color: "common.white",
        background: `
          radial-gradient(1000px 420px at 75% 8%, rgba(255,255,255,.12), rgba(255,255,255,0) 60%),
          linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 70%)
        `,
      }}
    >
      <DemoDialog open={openDemo} onClose={() => setOpenDemo(false)} />
      {/* نستخدم Section لكن بخلفية شفافة حتى يظهر تدرّج الـ Box */}
      <Section id="header" bgcolor="transparent" py={{ xs: 6, md: 10 }}>
        <Grid container spacing={4} alignItems="center">
          {/* عمود النصوص والأزرار */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 4 },
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.98)",
                color: "text.primary",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 22px 60px rgba(84,49,152,.18)",
              }}
            >
              {/* شارة أعلى العنوان */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.25,
                  py: 0.6,
                  mb: 1.25,
                  borderRadius: 999,
                  fontWeight: 800,
                  color: "primary.main",
                  bgcolor: "rgba(84,49,152,.08)",
                  border: "1px solid",
                  borderColor: "rgba(84,49,152,.2)",
                  fontSize: 13,
                }}
              >
                {hero.badge}
              </Box>

              <Typography
                variant="h2"
                fontWeight={900}
                gutterBottom
                sx={{ fontSize: { xs: 24, md: 32 } }}
              >
                {hero.title}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 2.5,
                  lineHeight: 1.9,
                  fontSize: { xs: 14.5, md: 15.5 },
                }}
              >
                {hero.subtitle}
              </Typography>

              <Stack
                direction="row"
                flexWrap="wrap"
                spacing={1.25}
                useFlexGap
                sx={{ mb: 2.5 }}
              >
                <Button
                  variant="contained"
                  color="warning"
                  size="medium"
                  sx={{ fontWeight: 800, px: 6 }}
                  onClick={() => setOpenDemo(true)}
                >
                  جرّب الآن مجانا
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  size="medium"
                  startIcon={<WhatsAppIcon />}
                  href={`https://wa.me/${CONTACT.whatsapp}`}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    fontWeight: 800,
                    borderColor: "success.main",
                    color: "success.contrastText",
                    bgcolor: "success.main",
                    "&:hover": {
                      bgcolor: "success.dark",
                      borderColor: "success.dark",
                    },
                  }}
                >
                  تواصل واتساب
                </Button>
              </Stack>

              {/* الأرقام السريعة */}
              <Grid container spacing={1.25}>
                {hero.stats.map((s) => (
                  <Grid key={s.label} size={{ xs: 12, sm: 4 }}>
                    <StatCard value={s.value} label={s.label} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* عمود المعاينة + بطاقتان: تحت بعض حتى md، وجنب بعض من lg */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Box
              sx={{
                p: { xs: 2.5, md: 4 },
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,.25)",
              }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "rgba(226,215,255,.8)",
                  background: "linear-gradient(180deg,#faf7ff,#f1eaff)",
                  display: "grid",
                  placeItems: "center",
                  aspectRatio: "16/10",
                  color: "primary.contrastText",
                  mb: 2.5,
                  overflow: "hidden", // مهم عشان الزوايا تكون بنفس الـ borderRadius
                }}
              >
                <img
                  src="/assets/images/1.png"
                  alt="POS واجهة نقطة البيع"
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>

              <Grid container spacing={1.25}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      height: "100%",
                      bgcolor: "background.paper",
                    }}
                    variant="outlined"
                  >
                    <Typography fontWeight={800} sx={{ mb: 0.5, fontSize: 15 }}>
                      ⚡ سهل وسريع
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      تقليل الأخطاء وتسريع عملية البيع.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      height: "100%",
                      bgcolor: "background.paper",
                    }}
                    variant="outlined"
                  >
                    <Typography fontWeight={800} sx={{ mb: 0.5, fontSize: 15 }}>
                      🧾 الفوترة الإلكترونية
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      متوافق مع متطلبات هيئة الزكاة والضريبة والجمارك.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Section>
    </Box>
  );
}
